import Component from '@glimmer/component'
import { action } from '@ember/object'
import { ActivityDescriptionBook } from 'teacher-ui-client/components/assignments-admin/activity-description/book-activity'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { dasherize } from '@ember/string'
import { fn } from '@ember/helper'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { ActivityDescriptionLesson } from 'teacher-ui-client/components/assignments-admin/activity-description/lesson-activity'
import { on } from '@ember/modifier'
import { service } from '@ember/service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { AssignmentStatus } from 'teacher-ui-client/components/assignments-admin/assignment-status'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { IntlService } from 'ember-intl'
import type Store from '@ember-data/store'
import UiButton from 'teacher-ui-client/components/ui-button'
import type Assignment from 'teacher-ui-client/models/assignment'
import type SessionService from 'teacher-ui-client/services/session'
import { t, formatDate } from 'ember-intl'
import type ReadAloudReview from 'teacher-ui-client/models/read-aloud-review'
import type Teacher from 'teacher-ui-client/models/teacher'
import { eq } from 'ember-truth-helpers'

interface Args {
  assignment: Assignment
  averageScore?: number
  showReviews?: boolean
  closeAssignmentAction: () => void
  editAssignmentAction: (id: string) => void
  openMode?: 'show' | 'edit'
  selectedAssignmentId?: Nullable<string>
  showAssignmentAction: (id: string) => void
  showEditAndExpireAssignment?: boolean
  subscriptionType: SubscriptionType
}

interface Signature {
  Element: HTMLTableRowElement
  Args: Args
}

export class AssignmentsTableRow extends Component<Signature> {
  @service intl!: IntlService

  @service session!: SessionService

  @service store!: Store

  get rowHoverClassNames() {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return 'hover:bg-oceany-blue-150'
      case SubscriptionType.Maths:
        return 'hover:bg-ms-green-100'
      case SubscriptionType.Writing:
        return 'hover:bg-wl-blue-100'
      default:
        return 'hover:bg-dusty-black-100'
    }
  }

  get rowExpandedClassNames() {
    switch (this.args.subscriptionType) {
      case SubscriptionType.Reading:
        return 'bg-oceany-blue-150'
      case SubscriptionType.Maths:
        return 'bg-ms-green-100'
      default:
        return 'bg-dusty-black-100'
    }
  }

  get assignment() {
    return this.args.assignment
  }

  get reviewsForAssignment() {
    return this.store
      .peekAll<ReadAloudReview>('read-aloud-review')
      .filter((review) => review.assignmentUuid === this.assignment.id)
  }

  get status() {
    return dasherize(this.assignment.status || '')
  }

  get isSelected() {
    return this.args.selectedAssignmentId === this.assignment.id
  }

  get isEditing() {
    return this.isSelected && this.args.openMode === 'edit'
  }

  get isShowing() {
    return this.isSelected && this.args.openMode === 'show'
  }

  get collapseRowLabel() {
    return this.intl.t('assignments.assignmentDetails.hide')
  }

  get expandRowLabel() {
    return this.intl.t('assignments.assignmentDetails.show')
  }

  get showEditButton() {
    return this.args.showEditAndExpireAssignment && this.status !== 'completed'
  }

  get editButtonTitle() {
    return this.isEditing ? this.intl.t('assignments.closeAssignment') : this.intl.t('assignments.editAssignment')
  }

  get assigner() {
    return this.store.peekAll<Teacher>('teacher').find((t) => t.id === String(this.assignment.assignerId))
  }

  get isAssignedByMe() {
    return String(this.session.currentTeacher.id) === String(this.assignment.assignerId)
  }

  get assignerName() {
    if (this.assigner) {
      if (this.isAssignedByMe) {
        return this.intl.t('assignments.me')
      }
      return this.assigner.fullName
    }
    return String(this.assignment.assignerId)
  }

  @action
  showAssignment(id: Nullable<string>) {
    if (!id) return
    if (this.isShowing) {
      this.args.closeAssignmentAction()
    } else {
      this.args.showAssignmentAction(id)
    }
  }

  @action
  editAssignment(id: Nullable<string>) {
    if (!id) return
    if (this.isEditing) {
      this.args.closeAssignmentAction()
    } else {
      this.args.editAssignmentAction(id)
    }
  }

  <template>
    <tr
      data-id={{this.assignment.id}}
      class="relative {{this.rowHoverClassNames}} {{if this.isSelected this.rowExpandedClassNames}}"
      ...attributes
    >
      <td data-sort="number" class="p-3 print:hidden">
        <UiButton
          data-test-show-assignment-btn
          @appearance={{ButtonAppearance.regular}}
          class="print:hidden"
          {{on "click" (fn this.showAssignment this.assignment.id)}}
          aria-label={{if
            this.isShowing
            (t "assignments.assignmentDetails.hide")
            (t "assignments.assignmentDetails.show")
          }}
        >
          <PhosphorIcon
            class="-mx-2"
            @icon={{if this.isShowing "phosphor-caret-down-fill" "phosphor-caret-right-fill"}}
          />
        </UiButton>
      </td>
      <td class="p-3" data-test-assignment-title>
        {{#if this.assignment.isLibraryAssignment}}
          <ActivityDescriptionBook @assignment={{this.assignment}} />
        {{else if this.assignment.isWritingAssignment}}
          <strong class="block text-left font-bold">{{this.assignment.title}} </strong>
          <small class="text-sm">
            {{#if (eq this.assignment.assignmentType "ewi")}}
              {{t "assignments.description.writing_legends.ewi"}}
            {{else if (eq this.assignment.assignmentType "long-write")}}
              {{t "assignments.description.writing_legends.long_write"}}
            {{/if}}
          </small>
        {{else}}
          <ActivityDescriptionLesson @assignment={{this.assignment}} />
        {{/if}}
      </td>
      <td class="p-3 text-left" data-test-assigner>
        {{this.assignerName}}
      </td>
      <td data-sort="date" class="p-3 text-left" data-test-assignment-start-date>
        {{formatDate this.assignment.startAt weekday="short" day="2-digit" month="short" year="2-digit"}}
      </td>
      <td data-sort="date" class="p-3 text-left" data-test-assignment-end-date>
        {{formatDate this.assignment.endAt weekday="short" day="2-digit" month="short" year="2-digit"}}
      </td>
      <td class="p-3 text-left" data-test-assignment-status>
        <AssignmentStatus @status={{this.status}} />
      </td>
      <td class="p-3 text-left" data-test-assignment-students-completed>
        {{this.assignment.studentsCompleted}}
        /
        {{this.assignment.studentsTotal}}
      </td>
      <td class="p-3 text-left" data-test-assignment-average-score>
        {{formatValue @averageScore append="%"}}
      </td>
      {{#if @showReviews}}
        <td class="p-3 text-left" data-test-read-aloud-reviews>
          {{#if this.reviewsForAssignment}}
            {{this.reviewsForAssignment.length}}
            /
            {{@assignment.studentsTotal}}
          {{else}}
            -
          {{/if}}
        </td>
      {{/if}}
      <td class="sticky top-0 right-0 p-3 print:hidden">
        {{#if this.showEditButton}}
          <UiButton
            data-test-edit-assignment-btn
            class="whitespace-nowrap"
            @appearance={{ButtonAppearance.muted}}
            {{on "click" (fn this.editAssignment this.assignment.id)}}
          >
            <PhosphorIcon @icon={{if this.isEditing "phosphor-x-bold" "phosphor-note-pencil-fill"}} />
            {{this.editButtonTitle}}
          </UiButton>
        {{/if}}
      </td>
    </tr>
  </template>
}
